/**
 * @file useDataGridOO Hook
 * @description Custom React hook for managing advanced data grid functionality with server-side pagination,
 * filtering, row selection, and toolbar integration. Returns a memoized Open Ocean DataGridOO component with full configuration support.
 * @module hooks/useDataGridOO
 *
 * @migrationNote
 * This hook is part of the refactored data grid table system introduced in the Open Ocean redesign.
 * Previously, the system used a single `index.tsx` file that exported a generic DataGrid component.
 * The architecture has been split into two specialized table types:
 *   1. **DataGridOO**: Main data table with full pagination, filtering, and row selection (this hook)
 *   2. **SubDataList**: Detail/child table with infinite scroll pagination for parent-child relationships
 * This separation allows for better code organization, cleaner APIs, and specialized optimizations for each use case.
 * The old `useDataGrid` hook has been renamed to `useDataGridOO` to clearly indicate it produces the Open Ocean table variant.
 * Both hooks share common server-side utilities via `useServerSide` but provide tailored component wrappers for their respective needs.
 */
import TableToolbar, { TableToolbarProps } from "@mlpa/core-ui-core-open_ocean_ui/Organisms/Toolbars/TableToolbar";
import {
  GridDensity,
  GridFilterFormProps,
  GridRowParams,
  GridRowProps,
  GridRowSelectionModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { EntityMappings } from "graphqlBase/__utils__/entityMappings";
import React, { useMemo } from "react";
import { useRecoilValue } from "recoil";
import translations from "translations";
import { appliedFiltersCounter } from "utils/appliedFiltersCounter";
import LoadingOverlay from "../LoadingOverlay";
import useServerSide from "../serverSide";
import { BaseRow, Column, DataGridOOWrapperProps, UseMakeDataGridProps } from "../serverSide/types";
import { StyledDataGridOOWrapper } from "../styles";
import useNoDataOverlayProps from "./useNoDataOverlayProps";
import { DataGridOOHeaderProps, NoItemsOverlayProps } from "@mlpa/core-ui-core-open_ocean_ui/Organisms/Tables";
import { AddIcon, FilterClearIcon } from "@mlpa/core-ui-core-open_ocean_ui";

/**
 * Props interface for the DataGridOO component returned by useDataGridOO hook.
 * Extends DataGridOOWrapperProps with configuration for rendering, interactions, and state.
 *
 * @template R - Generic type extending BaseRow representing the row data structure
 * @template E - Generic type key of EntityMappings representing the entity type
 *
 * @interface DataGridOOComponentProps
 * @extends {DataGridOOWrapperProps<R, E>}
 */
interface DataGridOOComponentProps<R extends BaseRow, E extends keyof EntityMappings>
  extends DataGridOOWrapperProps<R, E> {
  /** Enable checkbox column for row selection. @default true */
  checkboxSelection?: boolean;

  /** Callback when user requests deletion of selected rows (ids: string[]) */
  handleDeleteRows?: (ids: string[]) => void;

  /** Callback when user requests editing a row (id: string) */
  handleEditRow?: (id: string) => void;

  /** Callback when a row is clicked (params: GridRowParams) */
  handleOnRowClick?: (params: GridRowParams) => void;

  /** Show pagination controls at the bottom of the grid. @default true */
  hasPagination?: boolean;

  /** Show toolbar with filter and column options. @default true */
  hasToolbar?: boolean;

  /** Currently selected rows (controlled selection) */
  selectedRows?: GridRowSelectionModel | undefined;

  /** Callback to update tag modal visibility state */
  setIsTagModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;

  /** Callback to update selected rows */
  setSelectedRows?: (rows: GridRowSelectionModel | undefined) => void;

  /** Callback when mouse hovers over a row (params: GridRowProps) */
  onRowHover?: (params: GridRowProps) => void;

  /** Custom props for the toolbar component */
  toolbarProps?: TableToolbarProps;

  /** Default number of rows per page. @default 25 */
  pageSize?: number;

  /** Available page size options. @default [25, 50, 100] */
  pageSizeOptions?: number[];

  /** Show loading skeleton while fetching data. @default false */
  showSkeleton?: boolean;

  /** Custom props for filter form inputs (operator, column, value) */
  customFilterFormProps?: Pick<
    GridFilterFormProps,
    "operatorInputProps" | "columnInputProps" | "operatorInputProps" | "valueInputProps"
  >;

  /** Row density: 'compact' | 'standard' | 'comfortable'. @default "standard" */
  density?: GridDensity;

  /** Custom label for rows per page selector */
  rowsPerPageLabel?: string;

  /** Height of the grid container (e.g., "600px", "100%") */
  height?: string | number;
  noItemsOverlayProps?: NoItemsOverlayProps;
  /** Optional header label rendered above the grid (passed through to OO DataGridOO) */
  headerProps?: DataGridOOHeaderProps;
  minHeight?: string | number;
}

/**
 * Custom React hook for creating a fully-configured DataGridOO component with server-side features.
 * Integrates pagination, filtering, row selection, and deletion workflows into a memoized component.
 *
 * @template R - Generic type extending BaseRow
 * @template E - Generic type key of EntityMappings
 *
 * @param {Omit<UseMakeDataGridProps<R, E>, "defaultProps">} config - Grid configuration (columns, rows, apiRef, fetch, entity)
 *
 * @returns {Object} Object with DataGridOO component and serverSide utilities
 *
 * @example
 * const { DataGridOO } = useDataGridOO<DeviceRow, 'Device'>({
 *   columns: deviceColumns,
 *   rows: deviceRows,
 *   apiRef: gridApiRef,
 *   fetch: fetchDevices,
 *   entity: 'Device'
 * });
 *
 * return (
 *   <DataGridOO
 *     hasToolbar hasPagination checkboxSelection
 *     selectedRows={selected} setSelectedRows={setSelected}
 *     handleDeleteRows={onDelete}
 *   />
 * );
 *
 * @remarks
 * - Memoizes component to prevent unnecessary re-renders
 * - Integrates Recoil state for delete confirmation
 * - Server-side pagination (page-based, not infinite scroll)
 * - Automatic loading overlay and no-data state handling
 *
 * @see useServerSide
 * @see ConfirmAndDelete
 * @see TableToolbar
 */
const useDataGridOO = <R extends BaseRow, E extends keyof EntityMappings>(
  config: Omit<UseMakeDataGridProps<R, E>, "defaultProps">
) => {
  const apiRef = useGridApiRef();
  const serverSide = useServerSide<R, E>({ ...config, apiRef });

  /**
   * Memoized DataGridOO component with full configuration.
   * Renders the data grid with toolbar, pagination, row selection, and delete confirmation.
   *
   * @type {React.FC<DataGridOOComponentProps<R, E>>}
   *
   * @param {DataGridOOComponentProps<R, E>} props - Component props with all grid configuration
   * @returns {React.ReactElement} The rendered data grid component with toolbar and delete dialog
   *
   * @internal This component is memoized and only re-renders when serverSide dependency changes
   */
  const ExtendedDataGridOO: React.FC<DataGridOOComponentProps<R, E>> = useMemo(
    () =>
      ({
        apiRef,
        rows,
        columns,
        checkboxSelection = true,
        handleOnRowClick,
        selectedRows,
        hasPagination = true,
        hasToolbar = true,
        setIsTagModalOpen,
        setSelectedRows,
        handleDeleteRows,
        handleEditRow,
        pageSize = 25,
        pageSizeOptions = [25, 50, 100],
        onRowHover,
        customFilterFormProps,
        hideFooter,
        showSkeleton,
        rowsPerPageLabel,
        height,
        toolbarProps,
        autoHeight,
        minHeight = "70vh",
        headerProps,
        ...rest
      }) => {
        // Create loading overlay component with predefined props
        const LoadingOverlayWithProps = () => <LoadingOverlay tableType="dataGrid" />;

        /**
         * Handles row selection changes and propagates to parent component.
         * Preserves scroll position to prevent focus-driven scroll jumps with autoHeight.
         * @param {GridRowSelectionModel} rowSelectionModel - Updated selection model
         */
        const handleSetSelectedRows = (rowSelectionModel: GridRowSelectionModel) => {
          // Find the relevant scroll container to preserve position
          const rootEl = apiRef?.current?.rootElementRef?.current;
          let scrollParent: HTMLElement | null = null;

          if (autoHeight) {
            // For autoHeight, find the nearest ancestor that is actually scrolling
            // (not just styled with overflow:auto but without actual overflow)
            scrollParent = rootEl?.parentElement ?? null;
            while (scrollParent) {
              const { overflowY } = getComputedStyle(scrollParent);
              if (
                (overflowY === "auto" || overflowY === "scroll") &&
                scrollParent.scrollHeight > scrollParent.clientHeight
              )
                break;
              scrollParent = scrollParent.parentElement;
            }
          } else {
            // For fixed-height grids, use the DataGrid's internal virtual scroller
            scrollParent = rootEl?.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
          }

          const scrollTop = scrollParent?.scrollTop;

          setSelectedRows?.(rowSelectionModel);

          if (scrollParent && scrollTop !== undefined) {
            requestAnimationFrame(() => {
              scrollParent!.scrollTop = scrollTop;
            });
          }
        };

        /**
         * Resets all applied filters in the data grid
         */
        const handleResetFilters = () => apiRef?.current?.setFilterModel({ items: [] });

        // Get configuration for the no-data overlay with reset filter button
        const noFilterResultsOverlayProps = useNoDataOverlayProps(handleResetFilters);

        // Determine if loading skeleton should be displayed
        const shouldShowSkeleton = showSkeleton ?? false;
        /**
         * Memoized toolbar component with filter count display
         * Updates when appliedFilters changes or toolbar props change
         *
         * @returns {React.ReactElement} The configured table toolbar
         */
        const Toolbar = React.useCallback(() => {
          const filterModelItems = apiRef?.current?.state?.filter?.filterModel?.items;
          const appliedFilters = appliedFiltersCounter(
            filterModelItems && filterModelItems.length > 0 ? filterModelItems : []
          );
          const { deps, ...toolbarPropsInner } = {
            filterButtonProps: { label: translations.globals.table.filter, appliedFilters },

            columnOptionsButtonProps: { label: translations.globals.table.columnOptions },
            deps: [toolbarProps?.deps],
            ...toolbarProps,
          };
          return <TableToolbar showToolbar={hasToolbar} headerProps={headerProps} {...toolbarPropsInner} />;
        }, [hasToolbar, headerProps]);

        return (
          <>
            {/**@ts-ignore */}
            <StyledDataGridOOWrapper height={height} autoHeight={autoHeight}>
              <serverSide.DataGridOO
                {...rest}
                minHeight={minHeight}
                autoHeight={autoHeight}
                disableVirtualization={autoHeight}
                headerProps={headerProps}
                slots={{
                  toolbar: Toolbar,
                  loadingOverlay: LoadingOverlayWithProps,
                  filterPanelAddIcon: (props) => (
                    <AddIcon sx={{ height: "1.5rem", width: "1.5rem", marginRight: "-8px" }} onClick={props?.onClick} />
                  ),
                  filterPanelRemoveAllIcon: (props) => (
                    <FilterClearIcon
                      sx={{ height: "1.5rem", width: "1.5rem", marginRight: "-8px" }}
                      onClick={props?.onClick}
                    />
                  ),
                  ...rest.slots,
                }}
                noFilterResultsOverlayProps={noFilterResultsOverlayProps}
                rows={shouldShowSkeleton ? [] : rows}
                columns={columns as unknown as Column<R, E>[]}
                checkboxSelection={checkboxSelection}
                pagination={hasPagination ?? true}
                hideFooter={hideFooter}
                showToolbar
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: pageSize ?? 25,
                    },
                  },
                }}
                pageSizeOptions={pageSizeOptions}
                onRowClick={handleOnRowClick}
                onRowSelectionModelChange={handleSetSelectedRows}
                rowSelectionModel={selectedRows}
                loading={shouldShowSkeleton}
                paginationMode="server"
              />
            </StyledDataGridOOWrapper>
          </>
        );
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverSide]
  );

  /**
   * Return object containing server-side data management utilities and memoized grid component
   * @type {Object}
   * @property {...serverSide} - All server-side data fetching and state properties
   * @property {React.FC<DataGridOOComponentProps<R, E>>} DataGridOO - The configured OpenOcean grid component
   */
  return { ...serverSide, DataGridOO: ExtendedDataGridOO };
};

/**
 * Export the useDataGridOO hook as default export
 * @exports useDataGridOO
 */
export default useDataGridOO;
